import type { ReactNode } from 'react';
import './DraughtsRulePaper.css';

const INK = '#252725', PAPER = '#fffefa';
type Point = readonly [number, number];
type Piece = { at: Point; white?: boolean; king?: boolean; taken?: boolean };
type BoardProps = {
  name: string; rows: number; cols: number; unit: number; start: Point; pieces: Piece[];
  paths?: Point[][]; targets?: Point[]; finalRow?: boolean; label: string;
};

function DraughtsBoardPicture({name, rows, cols, unit, start, pieces, paths = [], targets = [], finalRow = false, label}: BoardProps) {
  const center = ([row, col]: Point) => [(col-start[1]+.5)*unit, (row-start[0]+.5)*unit];
  return <svg viewBox={`0 0 ${cols*unit} ${rows*unit}`} role="img" aria-label={label}
    data-draughts-board={name} data-rows={rows} data-cols={cols} data-cell-size={unit} data-start={start.join(',')}>
    {Array.from({length:rows}, (_,r) => Array.from({length:cols}, (_,c) =>
      <rect key={`${r},${c}`} x={c*unit} y={r*unit} width={unit} height={unit}
        fill={(r+start[0]+c+start[1])%2 ? '#d6ddd0' : '#f7f5ed'} stroke="#c5cbbf" strokeWidth=".4" />))}
    {finalRow && <rect width={cols*unit} height={unit} fill="#c5ae68" opacity=".24" />}
    {paths.map((points,n) => {
      const pointsOnBoard = points.map(center), [x,y] = pointsOnBoard.at(-1)!, [px,py] = pointsOnBoard.at(-2)!;
      const length = Math.hypot(x-px,y-py), dx=(x-px)/length, dy=(y-py)/length;
      return <g key={n} data-arrow-purpose="move">
        <polyline points={pointsOnBoard.map(p=>p.join(',')).join(' ')} fill="none" stroke="#68765b" strokeWidth="1.6" />
        <path d={`M${x-5*dx+3*dy} ${y-5*dy-3*dx}L${x} ${y}L${x-5*dx-3*dy} ${y-5*dy+3*dx}`} fill="none" stroke="#68765b" strokeWidth="1.6" />
      </g>;
    })}
    {targets.map((at,n) => {const [x,y]=center(at);return <circle key={n} cx={x} cy={y} r={unit*.32} fill="none" stroke="#68765b" strokeWidth="1.25" />;})}
    {pieces.map(({at,white,king,taken},n) => {
      const [x,y] = center(at);
      return <g key={n} data-piece={king?'king':'man'} data-square={at.join(',')} data-captured={taken||undefined}>
        <circle cx={x} cy={y} r={unit*.32} fill={white?PAPER:INK} stroke={INK} strokeWidth=".9" />
        {king && <text x={x} y={y+unit*.21} textAnchor="middle" fontSize={unit*.66} fill="#9c762e">♛</text>}
        {taken && <path d={`M${x-3} ${y-3}l6 6m0 -6l-6 6`} stroke="#b09d83" strokeWidth="1.1" />}
      </g>;
    })}
  </svg>;
}
function Pair({name,children}:{name:string;children:ReactNode}) {
  return <div className="draughts-pair" data-comparison={name}>{children}</div>;
}
function Panel({children,caption}:{children:ReactNode;caption:string}) {
  return <figure>{children}<figcaption>{caption}</figcaption></figure>;
}

function ManMoves() {
  return <Pair name="man-move-capture">
    <Panel caption="兵は斜め前へ1マス進みます。">
      <DraughtsBoardPicture name="man-step" rows={6} cols={6} unit={22} start={[4,0]} pieces={[{at:[5,4],white:true}]}
        paths={[[[5,4],[4,3]]]} targets={[[4,3],[4,5]]} label="6×6の同じ範囲の盤。白い兵は斜め前の空いた1マスへ進みます。" />
    </Panel>
    <Panel caption="取るときは後ろにも跳べます。">
      <DraughtsBoardPicture name="man-back-capture" rows={6} cols={6} unit={22} start={[4,0]} pieces={[{at:[5,4],white:true},{at:[6,3]}]}
        paths={[[[5,4],[7,2]]]} targets={[[7,2]]} label="左と同じ6×6の盤と22単位のマス。白い兵が斜め後ろの黒1枚を越え、そのすぐ先の空きマスに着地して取ります。" />
    </Panel>
  </Pair>;
}
function MajorityCapture() {
  const pieces:Piece[]=[{at:[5,2],white:true},{at:[7,8],white:true},{at:[4,1]},{at:[6,7]},{at:[4,5]}];
  return <Pair name="maximum-capture">
    <Panel caption="1枚だけの手は選べません。">
      <DraughtsBoardPicture name="capture-one" rows={10} cols={10} unit={14} start={[0,0]} pieces={pieces}
        paths={[[[5,2],[3,0]]]} targets={[[3,0]]} label="同じ10×10の全盤。左の白い兵からは黒1枚だけを取れますが、別の白い兵から2枚取れるので、この1枚取りは選べません。" />
    </Panel>
    <Panel caption="続けて2枚取ります。">
      <DraughtsBoardPicture name="capture-two" rows={10} cols={10} unit={14} start={[0,0]} pieces={pieces}
        paths={[[[7,8],[5,6],[3,4]]]} targets={[[5,6],[3,4]]} label="左と同じ10×10、同じ14単位のマス。右の白い兵が黒を1枚ずつ続けて2枚取る手が、全ての自分の駒の中で最多取りです。" />
    </Panel>
  </Pair>;
}
function Promotion() {
  return <Pair name="promotion">
    <Panel caption="最終段で手を終えた兵">
      <DraughtsBoardPicture name="promotion-man" rows={2} cols={3} unit={24} start={[0,0]} pieces={[{at:[0,1],white:true}]}
        finalRow label="2×3の同じ範囲。白い兵が相手側の最終段で手を終えました。" />
    </Panel>
    <Panel caption="ダムになります。">
      <DraughtsBoardPicture name="promotion-king" rows={2} cols={3} unit={24} start={[0,0]} pieces={[{at:[0,1],white:true,king:true}]}
        finalRow label="左と同じ2×3、同じ24単位のマス、同じ駒の位置。兵に王冠が加わりダムになります。駒は移動していません。" />
    </Panel>
  </Pair>;
}
function KingMoves() {
  return <Pair name="man-king-move">
    <Panel caption="兵は1マス">
      <DraughtsBoardPicture name="short-step" rows={6} cols={6} unit={22} start={[2,0]} pieces={[{at:[5,4],white:true}]}
        paths={[[[5,4],[4,3]]]} targets={[[4,3]]} label="6×6の盤。白い兵は斜め前へ1マス進みます。" />
    </Panel>
    <Panel caption="ダムは何マスでも">
      <DraughtsBoardPicture name="long-step" rows={6} cols={6} unit={22} start={[2,0]} pieces={[{at:[5,4],white:true,king:true}]}
        paths={[[[5,4],[2,1]],[[5,4],[7,2]]]} targets={[[2,1],[7,2]]} label="左と同じ6×6の盤、同じ22単位のマス。ダムは空いた斜めのマスを通って前にも後ろにも何マスでも動けます。" />
    </Panel>
  </Pair>;
}
function KingCapture() {
  return <figure className="draughts-king-capture" data-comparison="king-capture">
    <DraughtsBoardPicture name="king-capture-choice" rows={10} cols={10} unit={22} start={[0,0]}
      pieces={[{at:[9,0],white:true,king:true},{at:[6,3]},{at:[0,7]}]}
      paths={[[[9,0],[3,6]]]} targets={[[5,4],[3,6]]}
      label="10×10の全盤、22単位のマス。白いダムが途中に駒のない斜めの黒1枚を越えます。そのすぐ先と、さらに先の空きマスのどちらへ着地しても、この局面では1枚取りで選べます。" />
    <figcaption>相手の駒の先で、着地する空きマスを選べます。</figcaption>
  </figure>;
}
function CaptureRemoval() {
  return <div className="draughts-triple" data-comparison="capture-removal">
    <Panel caption="取る前">
      <DraughtsBoardPicture name="chain-before" rows={6} cols={6} unit={14} start={[4,0]} pieces={[{at:[8,1],white:true},{at:[7,2]},{at:[5,4]}]}
        paths={[[[8,1],[6,3]]]} targets={[[6,3]]} label="白が黒を2枚連続で取る前の6×6の範囲。" />
    </Panel>
    <Panel caption="取りの途中">
      <DraughtsBoardPicture name="chain-middle" rows={6} cols={6} unit={14} start={[4,0]} pieces={[{at:[6,3],white:true},{at:[7,2],taken:true},{at:[5,4]}]}
        paths={[[[6,3],[4,5]]]} targets={[[4,5]]} label="同じ範囲と尺度。最初に取った黒は盤上に残り、次の黒を取り続けます。同じ駒は再び跳べません。" />
    </Panel>
    <Panel caption="取り終えた後">
      <DraughtsBoardPicture name="chain-after" rows={6} cols={6} unit={14} start={[4,0]} pieces={[{at:[4,5],white:true}]}
        label="同じ範囲と尺度。連続取りが終わって、黒2枚を盤から除きます。" />
    </Panel>
  </div>;
}
function PassingLastRow() {
  return <Pair name="passing-last-row">
    <Panel caption="途中で最終段">
      <DraughtsBoardPicture name="pass-middle" rows={4} cols={8} unit={18} start={[0,2]} pieces={[{at:[0,5],white:true},{at:[1,4],taken:true},{at:[1,6]}]}
        paths={[[[0,5],[2,7]]]} targets={[[2,7]]} finalRow label="4×8の範囲。白い兵が取りの途中で最終段へ着きましたが、続いて別の黒を取り、別の段へ着地します。" />
    </Panel>
    <Panel caption="兵のままです。">
      <DraughtsBoardPicture name="pass-after" rows={4} cols={8} unit={18} start={[0,2]} pieces={[{at:[2,7],white:true}]}
        finalRow label="左と同じ範囲・尺度。最終段以外で取りを終えたので、王冠は付かず兵のままです。" />
    </Panel>
  </Pair>;
}

export function DraughtsRulePaper() {
  return <article className="rule-paper draughts-paper">
    <div className="draughts-primary" data-draughts-primary>
      <section className="draughts-basics">
        <h2>相手の駒をすべて取るか、動けなくすると勝ちです。</h2>
        <p className="rule-small">10×10の黒いマスを使い、白から交互に動かします。</p>
        <ManMoves />
        <p>斜めに隣り合う相手の駒1枚を飛び越え、すぐ先の空きマスに着地して取ります。</p>
      </section>
      <section className="draughts-force">
        <p>取れる駒があれば、必ず取ります。着地した先でも取れるなら、同じ駒で続けます。</p>
        <p>自分の駒すべての取り方を比べ、一度に最も多く取れる手を選びます。同じ数なら、どの手でも選べます。</p>
      </section>
    </div>
    <div className="draughts-details">
      <section data-draughts-detail="majority">
        <h3>最も多く取れる手を選びます。</h3><MajorityCapture />
      </section>
      <section data-draughts-detail="king">
        <h3>兵からダムへ</h3>
        <p>兵が相手側の一番奥の段で手を終えると、ダムになります。</p>
        <Promotion />
        <p>ダムは斜めの空いたマスを通って、前にも後ろにも何マスでも動けます。</p>
        <KingMoves />
      </section>
      <section data-draughts-detail="king-capture">
        <h3>ダムの取り</h3>
        <p>ダムは途中に別の駒がない相手の駒を斜めに1枚飛び越えます。その先の次の駒までの空いたマスへ着地して取ります。</p>
        <KingCapture />
        <p>ダムも連続取りと最多取りに従います。着地点も、最も多く取れる手に合わせて選びます。取る枚数が同じなら、兵とダムのどちらでも選べます。</p>
      </section>
      <section data-draughts-detail="removal">
        <h3>連続取りの途中では</h3>
        <p>取った駒は一連の取りが終わってから盤から除きます。同じ駒を2度飛び越すことはできません。</p>
        <CaptureRemoval />
        <p>途中で一番奥の段に着いても、別の段で手を終えた兵はダムになりません。</p>
        <PassingLastRow />
      </section>
    </div>
  </article>;
}
